import { useEffect, useState, useCallback } from 'react'
import { Activity } from 'lucide-react'
import { toast } from 'sonner'
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ActivityFeed } from '@/components/features/activity/ActivityFeed'
import { getActivityFeed, getActivityActors } from '@/lib/api/v2/activity'
import type { ActivityItemDto, ActivityActorDto } from '@/types'

// ── Period options ────────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { value: '7', label: '7 derniers jours' },
  { value: '30', label: '30 derniers jours' },
  { value: '90', label: '90 derniers jours' },
] as const

type PeriodValue = typeof PERIOD_OPTIONS[number]['value']

// ── ActivityPage ──────────────────────────────────────────────────────────────

export default function ActivityPage() {
  const [items, setItems] = useState<ActivityItemDto[]>([])
  const [actors, setActors] = useState<ActivityActorDto[]>([])
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<PeriodValue>('7')
  const [selectedActorId, setSelectedActorId] = useState<string>('all')

  const loadActors = useCallback(async () => {
    try {
      const res = await getActivityActors()
      setActors(res.actors)
    } catch {
      // Non-critical: filter will just show "Tous"
    }
  }, [])

  const loadFeed = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getActivityFeed({
        days: parseInt(period),
        actorUserId: selectedActorId !== 'all' ? selectedActorId : undefined,
      })
      setItems(res.items)
    } catch {
      setItems([])
      toast.error('Impossible de charger l\'activité')
    } finally {
      setLoading(false)
    }
  }, [period, selectedActorId])

  useEffect(() => {
    loadActors()
  }, [loadActors])

  useEffect(() => {
    loadFeed()
  }, [loadFeed])

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold">Activité de l'équipe</h1>
        </div>
        <Button variant="outline" size="sm" onClick={loadFeed} disabled={loading}>
          Actualiser
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Période :</span>
          <Select value={period} onValueChange={(v) => setPeriod(v as PeriodValue)}>
            <SelectTrigger className="w-44" data-testid="activity-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {actors.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Collaborateur :</span>
            <Select value={selectedActorId} onValueChange={setSelectedActorId}>
              <SelectTrigger className="w-44" data-testid="activity-actor-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {actors.map((actor) => (
                  <SelectItem key={actor.userId} value={actor.userId}>
                    {actor.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Feed */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {loading ? 'Chargement…' : `${items.length} événement${items.length !== 1 ? 's' : ''}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ActivityFeed
            items={items}
            loading={loading}
            emptyMessage={`Aucune activité sur les ${period} derniers jours`}
          />
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}
